/**
 * Utility functions for sale item warranty status and return tracking.
 * Uses item.inventory_serials / item.inventory_serial as the definitive source
 * of truth for serial return details and warranty claim eligibility.
 */

/**
 * Extracts all inventory serial objects from a sale item.
 * Supports item.inventory_serials and item.inventory_serial,
 * whether structured as a single object, an array of objects, or a JSON string.
 *
 * @param {object} item - Sale item or cart item
 * @returns {Array<object>} List of serial objects
 */
export function getInventorySerialObjects(item) {
  if (!item) return [];

  const rawCandidates = [
    item.inventory_serials,
    item.inventory_serial,
    item.serial,
    item.serials,
  ];

  const serialObjects = [];

  for (const candidate of rawCandidates) {
    if (!candidate) continue;

    let parsed = candidate;
    if (
      typeof candidate === "string" &&
      (candidate.startsWith("{") || candidate.startsWith("["))
    ) {
      try {
        parsed = JSON.parse(candidate);
      } catch {
        parsed = candidate;
      }
    }

    if (Array.isArray(parsed)) {
      for (const obj of parsed) {
        if (
          obj &&
          typeof obj === "object" &&
          (obj.serial_number || obj.status || obj.id)
        ) {
          serialObjects.push(obj);
        }
      }
    } else if (
      parsed &&
      typeof parsed === "object" &&
      (parsed.serial_number || parsed.status || parsed.id)
    ) {
      serialObjects.push(parsed);
    }
  }

  return serialObjects;
}

/**
 * Checks whether a sale item has already been returned or claimed under warranty.
 * Inspects items[0].inventory_serials / inventory_serial for return status details.
 *
 * @param {object} item - Sale item or cart item object
 * @returns {{
 *   isReturned: boolean,
 *   status: string,
 *   label: string,
 *   reason: string,
 *   serialNumber: string,
 *   serialId: string|null,
 *   returnDate: string|null,
 *   serialObj: object|null
 * }}
 */
export function getSaleItemReturnDetails(item) {
  if (!item) {
    return {
      isReturned: false,
      status: "",
      label: "",
      reason: "",
      serialNumber: "",
      serialId: null,
      returnDate: null,
      serialObj: null,
    };
  }

  // 1. Primary & definitive check: item.inventory_serials / item.inventory_serial
  const serialObjects = getInventorySerialObjects(item);

  for (const serialObj of serialObjects) {
    const serialStatus = (serialObj?.status || "").toLowerCase().trim();
    const statusLabel =
      serialObj?.status_info?.label ||
      (serialStatus ? serialStatus.charAt(0).toUpperCase() + serialStatus.slice(1).replace(/_/g, " ") : "");
    const serialNum =
      serialObj?.serial_number ||
      item.serial_number ||
      (Array.isArray(item.serial_numbers) && item.serial_numbers[0]) ||
      "";

    // Direct returned status check on inventory_serials
    if (serialStatus === "returned" || statusLabel.toLowerCase() === "returned") {
      return {
        isReturned: true,
        status: "returned",
        label: statusLabel || "Returned",
        reason: `Serial ${serialNum ? `"${serialNum}" ` : ""}is marked as Returned`,
        serialNumber: serialNum,
        serialId: serialObj?.id || null,
        returnDate: serialObj?.updated_at || item.updated_at || null,
        serialObj,
      };
    }

    // Statuses where the serial was returned for service/inspection/damage
    if (
      ["under_inspection", "refurbished", "damaged", "lost", "expired"].includes(serialStatus) &&
      serialObj?.is_available === false
    ) {
      return {
        isReturned: true,
        status: serialStatus,
        label: statusLabel || serialStatus.replace(/_/g, " "),
        reason: `Serial ${serialNum ? `"${serialNum}" ` : ""}is ${statusLabel || serialStatus}`,
        serialNumber: serialNum,
        serialId: serialObj?.id || null,
        returnDate: serialObj?.updated_at || item.updated_at || null,
        serialObj,
      };
    }
  }

  // Fallback checks for legacy / non-serialized items:
  // 2. Check item.warranty_status
  const warrantyStatus = (item.warranty_status || "").toLowerCase().trim();
  if (
    ["returned", "claimed", "in_claim", "replaced", "refunded", "void"].includes(
      warrantyStatus
    )
  ) {
    const displayLabel =
      warrantyStatus === "returned"
        ? "Returned"
        : warrantyStatus === "claimed" || warrantyStatus === "in_claim"
          ? "Claim Filed"
          : warrantyStatus.charAt(0).toUpperCase() + warrantyStatus.slice(1);
    return {
      isReturned: true,
      status: warrantyStatus,
      label: displayLabel,
      reason: `Warranty status is ${displayLabel}`,
      serialNumber: item.serial_number || "",
      serialId: null,
      returnDate: item.updated_at || null,
      serialObj: null,
    };
  }

  // 3. Check item.status or explicit boolean flags
  const itemStatus = (item.status || "").toLowerCase().trim();
  if (itemStatus === "returned" || item.is_returned === true) {
    return {
      isReturned: true,
      status: "returned",
      label: "Returned",
      reason: "Item status is Returned",
      serialNumber: item.serial_number || "",
      serialId: null,
      returnDate: item.updated_at || null,
      serialObj: null,
    };
  }

  // 4. Quantity returned check (for bulk/standard items)
  if (
    item.returned_quantity != null &&
    parseFloat(item.returned_quantity) > 0 &&
    parseFloat(item.returned_quantity) >= parseFloat(item.quantity || 1)
  ) {
    return {
      isReturned: true,
      status: "returned",
      label: "Returned",
      reason: `All items returned (${item.returned_quantity}/${item.quantity || 1})`,
      serialNumber: item.serial_number || "",
      serialId: null,
      returnDate: item.updated_at || null,
      serialObj: null,
    };
  }

  // 5. Direct warranty claim object or flags
  if (item.warranty_claim || item.has_active_claim) {
    return {
      isReturned: true,
      status: "claimed",
      label: "Claim Filed",
      reason: "An active warranty claim exists for this item",
      serialNumber: item.serial_number || "",
      serialId: null,
      returnDate: item.warranty_claim?.created_at || item.updated_at || null,
      serialObj: null,
    };
  }

  return {
    isReturned: false,
    status: "",
    label: "",
    reason: "",
    serialNumber: item.serial_number || "",
    serialId: null,
    returnDate: null,
    serialObj: null,
  };
}

/**
 * Checks if a sale item has already been returned or claimed under warranty.
 *
 * @param {object} item - Sale item or cart item
 * @returns {boolean}
 */
export function isItemWarrantyReturned(item) {
  return getSaleItemReturnDetails(item).isReturned;
}

/**
 * Returns warranty summary statistics for a sale.
 *
 * @param {object} sale - Sale object
 * @param {Array} [overrideItems] - Optional array of items to inspect
 * @returns {{
 *   totalItems: number,
 *   returnedItems: number,
 *   eligibleItems: number,
 *   allReturned: boolean,
 *   hasReturned: boolean
 * }}
 */
export function getSaleWarrantySummary(sale, overrideItems = null) {
  const items =
    overrideItems || sale?.items || sale?.sale_items || sale?.details || [];
  if (!Array.isArray(items) || items.length === 0) {
    return {
      totalItems: 0,
      returnedItems: 0,
      eligibleItems: 0,
      allReturned: false,
      hasReturned: false,
    };
  }

  let returnedCount = 0;
  for (const item of items) {
    if (isItemWarrantyReturned(item)) {
      returnedCount++;
    }
  }

  return {
    totalItems: items.length,
    returnedItems: returnedCount,
    eligibleItems: items.length - returnedCount,
    allReturned: returnedCount > 0 && returnedCount === items.length,
    hasReturned: returnedCount > 0,
  };
}

/**
 * Allowed return conditions for warranty claims according to API specification:
 * resaleable, defective, parts_only, no_return
 */
export const WARRANTY_RETURN_CONDITIONS = [
  { value: "defective", label: "Defective (-1 Stock, Damaged)" },
  { value: "resaleable", label: "Resaleable (+1 Stock, Refurbished)" },
  { value: "parts_only", label: "Parts Only (-1 Stock, Write-off)" },
  { value: "no_return", label: "No Return (No Stock Change)" },
];

/**
 * Allowed rejection reasons according to warranty API specification
 */
export const WARRANTY_REJECTION_REASONS = [
  { value: "out_of_warranty", label: "Out of Warranty" },
  { value: "accidental_damage", label: "Accidental Damage" },
  { value: "no_proof_of_purchase", label: "No Proof of Purchase" },
  { value: "unauthorised_repair", label: "Unauthorised Repair / Tampering" },
  { value: "consumable_wear", label: "Normal Consumable Wear" },
  { value: "policy_exclusion", label: "Policy Exclusion" },
  { value: "other", label: "Other" },
];

/**
 * Resolution types for warranty claims
 */
export const WARRANTY_RESOLUTION_TYPES = [
  { value: "replacement", label: "Replacement" },
  { value: "refund", label: "Refund" },
  { value: "repair", label: "Repair" },
  { value: "rejected", label: "Reject" },
];

/**
 * Extracts normalized status string from claim status field (which can be string or object)
 * @param {object} claim
 * @returns {string}
 */
export function getClaimStatusValue(claim) {
  if (!claim) return "";
  if (typeof claim.status === "object" && claim.status !== null) {
    return claim.status.value || "";
  }
  return claim.status || "";
}

/**
 * Extracts human-readable label from claim status field
 * @param {object} claim
 * @returns {string}
 */
export function getClaimStatusLabel(claim) {
  if (!claim) return "";
  if (typeof claim.status === "object" && claim.status !== null) {
    return claim.status.label || claim.status.value || "";
  }
  return claim.status || "";
}

/**
 * Safely extracts normalized document type string (e.g. "intake", "rejection", "exchange", "completion")
 * from a document record which may be a string, object with value/label/name/type, or enum.
 *
 * @param {object|string} doc - Document object or type string
 * @returns {string} Lowercase type string
 */
export function getDocumentType(doc) {
  if (!doc) return "";
  if (typeof doc === "string") return doc.toLowerCase().trim();

  const candidate =
    doc.document_type ??
    doc.type ??
    doc.doc_type ??
    doc.name ??
    "";

  if (typeof candidate === "string") {
    return candidate.toLowerCase().trim();
  }

  if (typeof candidate === "object" && candidate !== null) {
    const val =
      candidate.value ??
      candidate.name ??
      candidate.type ??
      candidate.slug ??
      candidate.label ??
      "";
    return String(val).toLowerCase().trim();
  }

  return String(candidate || "").toLowerCase().trim();
}

/**
 * Safely extracts human-readable label for a document record.
 *
 * @param {object|string} doc
 * @returns {string}
 */
export function getDocumentLabel(doc) {
  if (!doc) return "Document";
  if (typeof doc === "string") {
    return doc.charAt(0).toUpperCase() + doc.slice(1);
  }

  const candidate =
    doc.document_type ??
    doc.type ??
    doc.doc_type ??
    "";

  if (typeof candidate === "object" && candidate !== null) {
    if (candidate.label) return String(candidate.label);
    if (candidate.value) return String(candidate.value);
  }

  const typeStr = getDocumentType(doc);
  if (typeStr === "intake") return "Intake Receipt";
  if (typeStr === "rejection") return "Rejection Notice";
  if (typeStr === "exchange") return "Exchange Form";
  if (typeStr === "completion") return "Completion Certificate";

  return typeStr ? typeStr.charAt(0).toUpperCase() + typeStr.slice(1) : "Document";
}

/**
 * Checks if claim allows transitioning to given target(s).
 * Strictly checks status.allowed_transitions or claim.allowed_transitions.
 * Falls back to status matrix if allowed_transitions is not present.
 *
 * @param {object} claim
 * @param {...string} targets - Target transitions to check
 * @returns {boolean}
 */
export function canTransition(claim, ...targets) {
  if (!claim) return false;
  const rawTransitions =
    claim.status?.allowed_transitions ||
    claim.allowed_transitions ||
    [];

  const transitions = Array.isArray(rawTransitions)
    ? rawTransitions.map((t) => String(t).toLowerCase())
    : [];

  if (transitions.length > 0) {
    return targets.some((t) => transitions.includes(String(t).toLowerCase()));
  }

  // Fallback to Section 2 status-action matrix if allowed_transitions is absent from payload
  const s = getClaimStatusValue(claim).toLowerCase();
  return targets.some((t) => {
    const target = String(t).toLowerCase();
    if (s === "pending") {
      return ["under_assessment", "assess", "reject", "rejected"].includes(target);
    }
    if (s === "under_assessment") {
      return ["approved", "approve", "reject", "rejected"].includes(target);
    }
    if (s === "approved") {
      return ["in_progress", "resolve", "resolved", "fulfil", "fulfilled", "supplier_claim"].includes(target);
    }
    if (s === "in_progress") {
      return ["resolve", "resolved", "supplier_claim"].includes(target);
    }
    if (s === "resolved") {
      return ["close", "closed"].includes(target);
    }
    return false;
  });
}

/**
 * Parses a date or date-string (YYYY-MM-DD or ISO) into a local Date object without timezone drift.
 * @param {string|Date} dateStr
 * @returns {Date}
 */
export function parseLocalDate(dateStr) {
  if (!dateStr) return new Date();
  if (dateStr instanceof Date) return new Date(dateStr.getTime());
  const str = String(dateStr).split("T")[0];
  const parts = str.split("-");
  if (parts.length === 3) {
    const y = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10) - 1;
    const d = parseInt(parts[2], 10);
    if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
      return new Date(y, m, d);
    }
  }
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? new Date() : d;
}

/**
 * Formats a Date object to YYYY-MM-DD string using local calendar components.
 * @param {Date} d
 * @returns {string}
 */
export function formatLocalDate(d) {
  if (!d || isNaN(d.getTime())) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Calculates warranty start and end dates based on product warranty coverage.
 * Supports product.warranty (number) and product.warranty_timeframe ("Days", "Months", "Years", "Lifetime", etc.).
 * Defaults start date to provided customStartDate (or today) and end date to the calculated date.
 *
 * @param {object} product - Product object
 * @param {string|Date} [customStartDate=null] - Optional start date (e.g. sale date or item date)
 * @returns {{
 *   startDate: string,
 *   endDate: string,
 *   duration: number,
 *   timeframe: string,
 *   label: string,
 *   isNoWarranty: boolean
 * }}
 */
export function calculateWarrantyDates(product, customStartDate = null) {
  const start = parseLocalDate(customStartDate);
  const startDateStr = formatLocalDate(start);

  const duration = parseInt(
    product?.warranty ??
    product?.warranty_duration ??
    product?.warranty_period ??
    0,
    10,
  );

  const timeframe = (
    product?.warranty_timeframe ||
    product?.warranty_type ||
    product?.warranty_unit ||
    ""
  ).trim();

  const normalizedTf = timeframe.toLowerCase();

  // If explicitly "No Warranty" or "none"
  if (normalizedTf === "no warranty" || normalizedTf === "none") {
    return {
      startDate: startDateStr,
      endDate: startDateStr,
      duration: 0,
      timeframe: "No Warranty",
      label: "No Warranty",
      isNoWarranty: true,
    };
  }

  // If valid duration is set
  if (duration > 0 && timeframe) {
    const end = new Date(start.getTime());

    if (normalizedTf.includes("day")) {
      end.setDate(end.getDate() + duration);
    } else if (normalizedTf.includes("month")) {
      end.setMonth(end.getMonth() + duration);
    } else if (normalizedTf.includes("year")) {
      end.setFullYear(end.getFullYear() + duration);
    } else if (normalizedTf.includes("lifetime")) {
      end.setFullYear(end.getFullYear() + (duration > 0 ? duration : 10));
    } else {
      end.setFullYear(end.getFullYear() + duration);
    }

    const label = `${duration} ${timeframe}`;
    return {
      startDate: startDateStr,
      endDate: formatLocalDate(end),
      duration,
      timeframe,
      label,
      isNoWarranty: false,
    };
  }

  // If timeframe is Lifetime without explicit duration
  if (normalizedTf.includes("lifetime")) {
    const end = new Date(start.getTime());
    end.setFullYear(end.getFullYear() + 10);
    return {
      startDate: startDateStr,
      endDate: formatLocalDate(end),
      duration: 10,
      timeframe: "Lifetime",
      label: "Lifetime",
      isNoWarranty: false,
    };
  }

  // Fallback if product has no warranty coverage configured:
  // Default to 1 Year coverage
  const defaultEnd = new Date(start.getTime());
  defaultEnd.setFullYear(defaultEnd.getFullYear() + 1);

  return {
    startDate: startDateStr,
    endDate: formatLocalDate(defaultEnd),
    duration: 1,
    timeframe: "Years",
    label: "1 Year",
    isNoWarranty: false,
  };
}

/**
 * Extracts all serial number strings from a sale item or inventory object.
 * Checks inventory_serials, inventory_serial, serial_numbers, serial_number,
 * and getSaleItemReturnDetails.
 *
 * @param {object} item - Sale item or product item
 * @returns {Array<string>} Array of unique serial numbers
 */
export function getItemSerials(item) {
  if (!item) return [];
  const ret = getSaleItemReturnDetails(item);
  const serialSet = new Set();

  if (item.inventory_serials) {
    if (typeof item.inventory_serials === "object" && !Array.isArray(item.inventory_serials)) {
      if (item.inventory_serials.serial_number) {
        serialSet.add(String(item.inventory_serials.serial_number).trim());
      }
    } else if (Array.isArray(item.inventory_serials) && item.inventory_serials.length > 0) {
      item.inventory_serials.forEach((s) => {
        if (s?.serial_number) serialSet.add(String(s.serial_number).trim());
      });
    }
  }

  if (item.inventory_serial?.serial_number) {
    serialSet.add(String(item.inventory_serial.serial_number).trim());
  }

  if (Array.isArray(item.serial_numbers)) {
    item.serial_numbers.forEach((sn) => {
      if (sn) serialSet.add(String(sn).trim());
    });
  } else if (
    typeof item.serial_numbers === "string" &&
    item.serial_numbers !== "null" &&
    item.serial_numbers.trim() !== ""
  ) {
    try {
      const parsed = JSON.parse(item.serial_numbers);
      if (Array.isArray(parsed)) {
        parsed.forEach((sn) => {
          if (sn) serialSet.add(String(sn).trim());
        });
      } else if (parsed) {
        serialSet.add(String(parsed).trim());
      }
    } catch {
      serialSet.add(item.serial_numbers.trim());
    }
  }

  if (item.serial_number) {
    serialSet.add(String(item.serial_number).trim());
  }

  if (ret.serialNumber) {
    serialSet.add(String(ret.serialNumber).trim());
  }

  return Array.from(serialSet).filter(Boolean);
}


